/* eslint-disable complexity */
import BabyUIThemeProvider from 'baby-ui/foundation/BabyUIThemeProvider'
import { Helmet } from 'react-helmet'
import { withContextualizedTracking } from 'lib/analytics'
import useCurrentUser from 'shared/hooks/useCurrentUser/useCurrentUser'
import withReactQueryClientProvider from 'shared/HOC/withReactQueryClientProvider'
import LandingPageTheme from './LandingPage.theme'
import { LandingPageSections } from './LandingPage.types'
import BrandIconListSection from './components/BrandIconListSection/BrandIconListSection'
import HeroSection from './components/HeroSection/HeroSection'
import TextSection from './components/TextSection/TextSection'
import DividerSection from './components/DividerSection/DividerSection'
import CardsSection from './components/CardsSection/CardsSection'
import FeatureHighlightSection from './components/FeatureHighlightSection/FeatureHighlightSection'
import SpacerSection from './components/SpacerSection/SpacerSection'
import { usePage, useOnPageLoad } from './LandingPage.utils'
import FinalCTASection from './components/FinalCTASection/FinalCTASection'
import FrequentlyAskedQuestionsSection from './components/FrequentlyAskedQuestionsSection/FrequentlyAskedQuestionsSection'
import SchemaSection from './components/SchemaSection/SchemaSection'
import GuidesSection from './components/GuidesSection/GuidesSection'

interface LandingPageProps {
  slug: string
  page: {
    sections: LandingPageSections[]
  }
}

const LandingPage = ({ slug, page: { sections } }: LandingPageProps) => {
  const theme = new LandingPageTheme()
  const pageConfig = usePage(slug)
  const [currentUser] = useCurrentUser()
  useOnPageLoad(pageConfig)

  if (!pageConfig) return null

  const {
    title,
    description,
    canonicalUrl,
    additionalHelmetProps,
    sections: currentSections,
  } = pageConfig

  // Concatting here to keep structure for sections coming as props in future
  const allSections = currentSections
    ? sections.concat(currentSections)
    : sections || []

  return (
    <BabyUIThemeProvider theme={theme}>
      <Helmet>
        <title>{title}</title>
        <meta content={description} name="description" />
        <link href={canonicalUrl} rel="canonical" />
        {additionalHelmetProps}
      </Helmet>
      {allSections.map((section) => {
        const { type, id, isVisible } = section

        if (typeof isVisible === 'function' && !isVisible(currentUser)) {
          return null
        }

        switch (type) {
          case 'hero':
            return <HeroSection key={id} section={section} />
          case 'text':
            return <TextSection key={id} section={section} />
          case 'divider':
            return <DividerSection key={id} section={section} />
          case 'cards':
            return <CardsSection key={id} section={section} />
          case 'feature_highlight':
            return <FeatureHighlightSection key={id} section={section} />
          case 'spacer':
            return <SpacerSection key={id} />
          case 'brand_icon_list':
            return <BrandIconListSection key={id} section={section} />
          case 'final_cta':
            return <FinalCTASection key={id} section={section} />
          case 'frequently_asked_questions':
            return (
              <FrequentlyAskedQuestionsSection key={id} section={section} />
            )
          case 'guides':
            return <GuidesSection key={id} section={section} />
          case 'schema':
            return <SchemaSection key={id} section={section} />
          default:
            return null
        }
      })}
    </BabyUIThemeProvider>
  )
}

export default withContextualizedTracking()(
  withReactQueryClientProvider(LandingPage)
)
